import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, Check, X, Upload, ChevronDown, Plus, FileText, Calendar, Building2, CreditCard, Eye } from 'lucide-react';
import { financialPowersAPI, financialPowerInvoicesAPI, vendorInvoicesAPI } from '../services/api';
import { formatCurrency, getCurrencyFontClass } from '../utils/currency';
import { User } from '../types';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import AttachmentViewer from '../components/AttachmentViewer';
import { decryptData } from '../utils/encryption';

interface RelatedInvoice {
  id: number;
  reference_number?: string;
  invoice_type?: string;
  amount?: number | string | null;
  status: string;
  attachment_url?: string;
  attachment_size?: number;
  attachment_filename?: string;
  additional_attachments?: Array<{
    filename?: string;
    url?: string;
    size?: number;
    type?: string;
  }>;
  approvers?: number[] | null;
  approver_details?: Array<{
    id: number;
    approver_id: number;
    approver_order: number;
    status: string;
    first_name: string;
    last_name: string;
    email: string;
    notes?: string | null;
    approved_at?: string | null;
  }>;
  created_at: string;
  updated_at: string;
}

interface Proposal {
  id: number;
  department_id?: number;
  vendor_id?: number;
  invoice_type: string;
  currency: string;
  amount: string | number;
  reference_number?: string;
  reference_name?: string;
  status: string;
  created_by?: number;
  created_at: string;
  department_name?: string;
  vendor_name?: string;
  approver_details?: Array<{
    id: number;
    approver_id: number;
    approver_order: number;
    status: string;
    first_name: string;
    last_name: string;
    email: string;
  }>;
  related_invoices?: RelatedInvoice[];
  invoice_count?: number;
}

interface CreatedInvoice {
  id: number;
  reference_number?: string;
  status: string;
  attachment_url?: string;
  attachment_filename?: string;
  attachment_size?: number;
  created_at: string;
  approver_details?: Array<{
    first_name: string;
    last_name: string;
    email: string;
    approver_order: number;
    status: string;
  }>;
  approvers?: number[];
}

const CreateFinancialPowerInvoice: React.FC = () => {
  const navigate = useNavigate();
  const userPermissionsLocalStorage = localStorage.getItem('permissions') || '';
  const userPermissions = decryptData(userPermissionsLocalStorage);

  const [referenceNumber, setReferenceNumber] = useState('');
  const [searching, setSearching] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [createdInvoice, setCreatedInvoice] = useState<CreatedInvoice | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [additionalAttachments, setAdditionalAttachments] = useState<File[]>([]);
  const [approvers, setApprovers] = useState<string[]>(['']);
  const [approverSearchTerms, setApproverSearchTerms] = useState<string[]>(['']);
  const [approverDropdownOpenIndex, setApproverDropdownOpenIndex] = useState<number | null>(null);
  const [availableApprovers, setAvailableApprovers] = useState<User[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalAttachmentsRef = useRef<HTMLInputElement>(null);
  const approverDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
  const invoiceDetailsRef = useRef<HTMLDivElement>(null);

  // Attachment viewer modal states
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState<string>('');
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string>('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!referenceNumber.trim()) {
      errorModal('Please enter a proposal reference number');
      return;
    }

    setSearching(true);
    try {
      // Fetch proposal by reference number using the dedicated API endpoint
      const response = await financialPowersAPI.getByReference(referenceNumber.trim());

      if (response?.success) {
        const proposal = response.data;

        if (proposal) {
          // Check if proposal is approved
          if (proposal.status === 'approved') {
            setProposal(proposal);
          } else {
            errorModal('This proposal is not approved yet. Only approved proposals can be used to create invoices.');
            setProposal(null);
          }
        } else {
          errorModal('No proposal found with this reference number');
          setProposal(null);
        }
      } else {
        errorModal(response?.error || 'Failed to search for proposal');
      }
      } catch (error: any) {
      console.error('Search error:', error);
      const errorMessage = error?.response?.data?.error || 
                          error?.response?.data?.message || 
                          'Failed to search for proposal';
      errorModal(errorMessage);
      setProposal(null);
    } finally {
      setSearching(false);
    }
  };

  const handleClear = () => {
    setProposal(null);
    setReferenceNumber('');
  };

  const handleCreateInvoice = () => {
    if (proposal) {
      setShowCreateModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setInvoiceFile(null);
    setAdditionalAttachments([]);
    setApprovers(['']);
    setApproverSearchTerms(['']);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (additionalAttachmentsRef.current) {
      additionalAttachmentsRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setInvoiceFile(file);
  };

  const handleSubmitInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoiceFile) {
      errorModal('Please select an invoice file');
      return;
    }

    if (!proposal) {
      errorModal('Proposal not found');
      return;
    }

    if (!proposal.reference_number) {
      errorModal('Proposal reference number is required');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('reference_number', proposal.reference_number);
      formData.append('attachment', invoiceFile);
      
      // Only invoice file is required during creation
      // Additional attachments and approvers will be added later via update
      
      const response = await financialPowerInvoicesAPI.create(formData);
      
      if (response?.success) {
        toast.success('Invoice created successfully');
        
        // Fetch the created invoice details
        if (response.data?.id) {
          try {
            const invoiceResponse = await financialPowerInvoicesAPI.getById(response.data.id);
            if (invoiceResponse?.success) {
              const invoiceData = invoiceResponse.data;
              setCreatedInvoice(invoiceData);
              
              // Load existing approvers if any
              if (invoiceData.approvers && Array.isArray(invoiceData.approvers) && invoiceData.approvers.length > 0) {
                setApprovers(invoiceData.approvers.map((a: number) => a.toString()));
                setApproverSearchTerms(invoiceData.approvers.map(() => ''));
              }
              
              // Scroll to invoice details section
              setTimeout(() => {
                invoiceDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }
          } catch (err) {
            console.error('Failed to fetch invoice details', err);
          }
        }
        
        handleCloseModal();
      } else {
        errorModal(response?.error || 'Failed to create invoice');
      }
    } catch (error: any) {
      console.error('Failed to create invoice:', error);
      const errorMessage = error?.response?.data?.error || 
                          error?.response?.data?.message || 
                          'Failed to create invoice';
      errorModal(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateInvoice = async () => {
    if (!createdInvoice) return;

    // Validate amount
    if (!amount.trim()) {
      errorModal('Amount is required');
      return;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      errorModal('Please enter a valid amount');
      return;
    }

    // Validate approvers for variable payment
    if (isVariablePayment()) {
      const validApprovers = approvers.filter(a => a !== '');
      // Check if invoice already has approvers assigned
      const hasExistingApprovers = createdInvoice.approvers && createdInvoice.approvers.length > 0;
      
      // If no existing approvers and no new approvers selected, require them
      if (!hasExistingApprovers && validApprovers.length === 0) {
        errorModal('At least one approver is required for variable payment invoices');
        return;
      }
    }

    setUpdating(true);
    try {
      const formData = new FormData();

      // Add amount
      formData.append('amount', amount);

      // Add additional attachments if any
      additionalAttachments.forEach((file) => {
        formData.append('additional_attachments', file);
      });

      // For variable payment, update approvers if assigned
      if (isVariablePayment() && approvers.some(a => a !== '')) {
        const validApprovers = approvers.filter(a => a !== '').map(a => parseInt(a));
        if (validApprovers.length > 0) {
          formData.append('approvers', JSON.stringify(validApprovers));
        }
      }

      const response = await financialPowerInvoicesAPI.update(createdInvoice.id, formData);
      
      if (response?.success) {
        toast.success('Invoice sent for approval successfully');
        
        // Refresh invoice details
        const invoiceResponse = await financialPowerInvoicesAPI.getById(createdInvoice.id);
        if (invoiceResponse?.success) {
          setCreatedInvoice(invoiceResponse.data);
        }
        
        // Refresh proposal data to get updated related_invoices
        if (proposal?.reference_number) {
          try {
            const proposalResponse = await financialPowersAPI.getByReference(proposal.reference_number);
            if (proposalResponse?.success && proposalResponse.data) {
              setProposal(proposalResponse.data);
            }
          } catch (err) {
            console.error('Failed to refresh proposal data', err);
          }
        }
        
        // Hide invoice details section
        setCreatedInvoice(null);
        setAdditionalAttachments([]);
        setApprovers(['']);
        setApproverSearchTerms(['']);
        setAmount('');
      } else {
        errorModal(response?.error || 'Failed to send invoice for approval');
      }
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      errorModal(error?.response?.data?.error || 'Failed to update invoice');
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    const fetchApprovers = async () => {
      try {
        const response = await vendorInvoicesAPI.invoiceApproverDropdown();
        if (response?.success) {
          setAvailableApprovers(response.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch approvers', error);
      }
    };
    fetchApprovers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        approverDropdownOpenIndex !== null &&
        approverDropdownRefs.current[approverDropdownOpenIndex] &&
        !approverDropdownRefs.current[approverDropdownOpenIndex]?.contains(event.target as Node)
      ) {
        setApproverDropdownOpenIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [approverDropdownOpenIndex]);

  const formatInvoiceType = (type: string): string => {
    if (!type) return 'N/A';
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() + ' Payment';
  };

  const isVariablePayment = () => {
    return proposal?.invoice_type?.toLowerCase() === 'variable';
  };

  const handleAdditionalAttachmentsChange = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const existing = [...additionalAttachments];
    Array.from(files).forEach((file) => {
      if (existing.length < 20) {
        existing.push(file);
      }
    });
    setAdditionalAttachments(existing);

    if (additionalAttachmentsRef.current) {
      additionalAttachmentsRef.current.value = '';
    }
  };

  const handleRemoveAdditionalAttachment = (index: number) => {
    setAdditionalAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddApprover = () => {
    if (approvers.length < 10) {
      setApprovers(prev => [...prev, '']);
      setApproverSearchTerms(prev => [...prev, '']);
    }
  };

  const handleRemoveApprover = (index: number) => {
    if (approvers.length > 1) {
      setApprovers(prev => prev.filter((_, i) => i !== index));
      setApproverSearchTerms(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleApproverSelect = (index: number, approverId: string) => {
    const newApprovers = [...approvers];
    newApprovers[index] = approverId;
    setApprovers(newApprovers);
    setApproverDropdownOpenIndex(null);
    setApproverSearchTerms(prev => {
      const newTerms = [...prev];
      newTerms[index] = '';
      return newTerms;
    });
  };

  // Handle viewing attachment in modal
  const handleViewAttachment = (url: string, filename: string) => {
    if (!url) {
      errorModal('No attachment URL available');
      return;
    }
    setCurrentAttachmentUrl(url);
    setCurrentAttachmentName(filename);
    setShowAttachmentViewer(true);
  };

  const closeAttachmentViewer = () => {
    setShowAttachmentViewer(false);
    setCurrentAttachmentUrl('');
    setCurrentAttachmentName('');
  };

  return (
    <div className="space-y-6">
      {/* Back Link */}
      <button
        onClick={() => navigate('/financial-power-invoice')}
        className="flex items-center text-primary-600 hover:text-primary-700 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        <span>Back to Invoices</span>
      </button>

      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{(userPermissions === "admin" || userPermissions === "finance_team" || userPermissions === "payment_approver") ? "View All Invoices" : "Create New Invoice"}</h1>
        <p className="mt-2 text-sm text-gray-600">
          Search for a proposal by reference number to create an invoice.
        </p>
      </div>

      {/* Search Component */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSearch}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reference Number
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                className="input-field flex-1"
                placeholder="Enter proposal reference number (e.g., FPP-2025-001)"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                disabled={searching}
              />
              <button
                type="submit"
                disabled={searching}
                className="btn-primary flex items-center space-x-2 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search className="h-4 w-4" />
                <span>{searching ? 'Searching...' : 'Search'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Proposal Details Card */}
      {proposal && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-primary-600 to-purple-600 px-6 py-4">
            <h3 className="text-xl font-semibold text-white">Proposal Details</h3>
          </div>

          {/* Content */}
          <div className="p-6">


            {/* Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reference Number</p>
                  <p className="text-sm font-medium text-gray-900">{proposal.reference_number || 'N/A'}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Vendor Name</p>
                  <p className="text-sm font-medium text-gray-900">{proposal.vendor_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Amount</p>
                  <p className={`text-sm font-medium text-gray-900 ${getCurrencyFontClass(proposal.currency)}`}>
                    {formatCurrency(proposal.amount, proposal.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Request Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(proposal.created_at).toLocaleDateString('en-GB')}
                  </p>
                </div>


              </div>

              {/* Right Column */}
              <div className="space-y-4">
              <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reference Name</p>
                  <p className="text-sm font-medium text-gray-900">{proposal.reference_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Department</p>
                  <p className="text-sm font-medium text-gray-900">{proposal.department_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Invoice Type</p>
                  <p className="text-sm font-medium text-gray-900">{formatInvoiceType(proposal.invoice_type)}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Approved
                    </span>
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Approvers Section */}
            {proposal.approver_details && proposal.approver_details.length > 0 && (
              <div className="mb-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Approvers</p>
                <div className="space-y-3">
                  {proposal.approver_details.map((approver, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {approver.first_name} {approver.last_name}
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">{approver.email}</p>
                      </div>
                      <div className="ml-4">
                        <span className="text-xs text-gray-500">Level {index + 1}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          (approver.status === 'approved' || approver.status === 'paid')
                            ? 'bg-green-100 text-green-800'
                            : approver.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {approver.status.charAt(0).toUpperCase() + approver.status.slice(1).toLowerCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Related Invoices Section */}
            {proposal.related_invoices && proposal.related_invoices.length > 0 && (
              <div className="mb-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Upload className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Related Invoices</h3>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {proposal.invoice_count || proposal.related_invoices.length} invoice(s)
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {proposal.related_invoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="bg-white rounded-lg border border-gray-200 shadow-sm p-6"
                    >
                      {/* Key Information Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-5">
                        {/* Invoice ID */}
                        <div className="p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="p-1.5 bg-pink-100 rounded-md flex-shrink-0">
                              <FileText className="h-4 w-4 text-pink-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Invoice ID</p>
                              <p className="text-sm font-semibold text-gray-900">FPI-{invoice.id}</p>
                            </div>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="p-1.5 bg-purple-100 rounded-md flex-shrink-0">
                              <Check className="h-4 w-4 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Status</p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                (invoice.status === 'approved' || invoice.status === 'paid')
                                  ? 'bg-green-100 text-green-800'
                                  : invoice.status === 'rejected'
                                  ? 'bg-red-100 text-red-800' 
                                  : invoice.status === 'finance_pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : invoice.status === 'payment_pending'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>{invoice.status === "finance_pending" ? "Pending at finance team" : invoice.status === "payment_pending" ? "Pending at payment processor" : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1).toLowerCase()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Invoice Type */}
                        <div className="p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="p-1.5 bg-green-100 rounded-md flex-shrink-0">
                              <Building2 className="h-4 w-4 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Invoice Type</p>
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {formatInvoiceType(invoice.invoice_type || '')}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="p-1.5 bg-orange-100 rounded-md flex-shrink-0">
                              <CreditCard className="h-4 w-4 text-orange-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Amount</p>
                              {invoice.amount ? (
                                <p className={`text-sm font-semibold text-gray-900 truncate ${getCurrencyFontClass(proposal.currency)}`}>
                                  {formatCurrency(invoice.amount, proposal.currency)}
                                </p>
                              ) : (
                                <p className="text-sm font-semibold text-gray-500">-</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Created Date */}
                        <div className="p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="p-1.5 bg-blue-100 rounded-md flex-shrink-0">
                              <Calendar className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Created Date</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {new Date(invoice.created_at).toLocaleDateString('en-GB')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Invoice File */}
                      {invoice.attachment_url && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="p-1.5 bg-gray-100 rounded-md flex-shrink-0">
                                <FileText className="h-4 w-4 text-gray-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {invoice.attachment_filename || invoice.attachment_url.split('/').pop() || 'Invoice File'}
                                </p>
                                {invoice.attachment_size && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {(invoice.attachment_size / 1024).toFixed(1)} KB
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => handleViewAttachment(invoice.attachment_url || '', invoice.attachment_filename || 'Invoice File')}
                              className="ml-3 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center space-x-1.5 transition-colors flex-shrink-0 shadow-sm"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              <span className="text-xs font-medium">View</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Approvers Section */}
                      {invoice.approver_details && invoice.approver_details.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center space-x-2 mb-3">
                            <Check className="h-4 w-4 text-gray-600" />
                            <h4 className="text-sm font-semibold text-gray-900">Approvers</h4>
                            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                              {invoice.approver_details.length} approver(s)
                            </span>
                          </div>
                          <div className="space-y-2">
                            {invoice.approver_details
                              .sort((a, b) => a.approver_order - b.approver_order)
                              .map((approver, idx) => (
                                <div
                                  key={approver.id || idx}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                                >
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                      {approver.first_name} {approver.last_name}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-0.5">{approver.email}</p>
                                  </div>
                                  <div className="ml-4 flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">Level {approver.approver_order}</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      approver.status === 'approved' 
                                        ? 'bg-green-100 text-green-800'
                                        : approver.status === 'rejected'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {approver.status}
                                    </span>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Additional Attachments */}
                      {invoice.additional_attachments && invoice.additional_attachments.length > 0 && (
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <Upload className="h-4 w-4 text-gray-600" />
                            <h4 className="text-sm font-semibold text-gray-900">Additional Attachments</h4>
                            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                              {invoice.additional_attachments.length} file(s)
                            </span>
                          </div>
                          <div className="space-y-2">
                            {invoice.additional_attachments.map((attachment, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
                              >
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                  <div className="p-1.5 bg-gray-100 rounded-md flex-shrink-0">
                                    <FileText className="h-4 w-4 text-gray-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {attachment.filename || 'Attachment'}
                                    </p>
                                    {attachment.size && (
                                      <p className="text-xs text-gray-500 mt-0.5">
                                        {(attachment.size / 1024).toFixed(1)} KB
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {attachment.url && (
                                  <button
                                    onClick={() => handleViewAttachment(attachment.url || '', attachment.filename || 'Attachment')}
                                    className="ml-3 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center space-x-1.5 transition-colors flex-shrink-0 shadow-sm"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    <span className="text-xs font-medium">View</span>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleClear}
                className="btn-secondary"
              >
                Clear
              </button>
              {userPermissions === "invoice_initiator" && !createdInvoice && (
                <button
                  onClick={handleCreateInvoice}
                  className="btn-primary"
                >
                  Create Invoice
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Details Card - Shown after successful upload */}
      {createdInvoice && proposal && (
        <div ref={invoiceDetailsRef} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-primary-600 to-purple-600 px-6 py-4">
            <h3 className="text-xl font-semibold text-white">Invoice Details</h3>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Invoice ID</p>
                  <p className="text-sm font-medium text-gray-900">FPI-{createdInvoice.id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reference Number</p>
                  <p className="text-sm font-medium text-gray-900">{createdInvoice.reference_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    createdInvoice.status === 'approved' 
                      ? 'bg-green-100 text-green-800'
                      : createdInvoice.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {createdInvoice.status == "finance_pending" ? "Finance Pending" : createdInvoice.status}
                  </span>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Created Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(createdInvoice.created_at).toLocaleDateString('en-GB')}
                  </p>
                </div>
                {amount && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Amount</p>
                    <p className={`text-sm font-medium text-gray-900 ${getCurrencyFontClass(proposal.currency || 'GBP')}`}>
                      {formatCurrency(parseFloat(amount), proposal.currency || 'GBP')}
                    </p>
                  </div>
                )}
                {createdInvoice.attachment_filename && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Invoice File</p>
                    <div className="flex items-center space-x-2">
                      <Upload className="h-4 w-4 text-gray-400" />
                      <button
                        onClick={() => handleViewAttachment(createdInvoice.attachment_url || '', createdInvoice.attachment_filename || 'Invoice File')}
                        className="text-sm text-primary-600 hover:text-primary-700 underline text-left"
                      >
                        {createdInvoice.attachment_filename}
                      </button>
                      {createdInvoice.attachment_size && (
                        <span className="text-xs text-gray-500">
                          ({(createdInvoice.attachment_size / 1024).toFixed(1)} KB)
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Amount Field */}
            <div className="mb-6 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{proposal.currency || 'GBP'}</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`input-field flex-1 ${getCurrencyFontClass(proposal.currency || 'GBP')}`}
                  placeholder="Enter invoice amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter the invoice amount in {proposal.currency || 'GBP'}
              </p>
            </div>

            {/* Additional Attachments Section */}
            <div className="mb-6 pt-6 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Attachments
              </label>
              <input
                ref={additionalAttachmentsRef}
                type="file"
                multiple
                className="input-field"
                onChange={(e) => handleAdditionalAttachmentsChange(e.target.files)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.csv"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional supporting documents — up to 20 files
              </p>
              {additionalAttachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {additionalAttachments.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700"
                    >
                      <span className="truncate pr-3">{file.name}</span>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-500"
                        onClick={() => handleRemoveAdditionalAttachment(idx)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Approvers Section - Only for Variable Payment */}
            {isVariablePayment() && (
              <div className="mb-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Approvers <span className="text-red-500">*</span>
                  </label>
                  {approvers.length < 10 && (
                    <button
                      type="button"
                      onClick={handleAddApprover}
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add Approver</span>
                    </button>
                  )}
                </div>
                <div className="space-y-3 min-h-24">
                  {approvers.map((approverId, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div ref={(el) => (approverDropdownRefs.current[index] = el)} className="relative flex-1">
                        <button
                          type="button"
                          className="input-field w-full flex items-center justify-between text-left"
                          onClick={() => {
                            setApproverDropdownOpenIndex(approverDropdownOpenIndex === index ? null : index);
                          }}
                        >
                          <span className={approverId ? 'text-gray-900' : 'text-gray-500'}>
                            {approverId
                              ? availableApprovers.find((a) => a.id.toString() === approverId)?.first_name + ' ' + 
                                availableApprovers.find((a) => a.id.toString() === approverId)?.last_name + 
                                ` (${availableApprovers.find((a) => a.id.toString() === approverId)?.email})`
                              : `Select Approver ${index + 1}`}
                          </span>
                          <ChevronDown
                            className={`h-4 w-4 text-gray-400 transition-transform ${
                              approverDropdownOpenIndex === index ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {approverDropdownOpenIndex === index && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg flex flex-col" style={{ maxHeight: '500px' }}>
                            <div className="p-2 border-b border-gray-100 flex-shrink-0">
                              <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                <input
                                  type="text"
                                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  placeholder="Search approvers..."
                                  value={approverSearchTerms[index] || ''}
                                  onChange={(e) => {
                                    const newTerms = [...approverSearchTerms];
                                    newTerms[index] = e.target.value;
                                    setApproverSearchTerms(newTerms);
                                  }}
                                />
                              </div>
                            </div>
                            <div 
                              className="overflow-y-auto overscroll-contain" 
                              style={{ 
                                maxHeight: '400px', 
                                minHeight: '150px',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e1 #f1f5f9'
                              }}
                            >
                              {availableApprovers
                                .filter((user) => {
                                  const searchTerm = (approverSearchTerms[index] || '').toLowerCase();
                                  const fullName = `${user.first_name} ${user.last_name} ${user.email || ''}`.toLowerCase();
                                  return fullName.includes(searchTerm);
                                })
                                .map((user) => (
                                  <button
                                    key={user.id}
                                    type="button"
                                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 transition-colors"
                                    onClick={() => handleApproverSelect(index, user.id.toString())}
                                  >
                                    {user.first_name} {user.last_name} ({user.email})
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {approvers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveApprover(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  For variable payment, approvers must approve before invoice goes to finance team
                </p>
              </div>
            )}

            {/* Approvers Display - Show assigned approvers */}
            {createdInvoice.approver_details && createdInvoice.approver_details.length > 0 && (
              <div className="mb-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-4">Assigned Approvers</p>
                <div className="space-y-3">
                  {createdInvoice.approver_details.map((approver, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {approver.first_name} {approver.last_name}
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">{approver.email}</p>
                      </div>
                      <div className="ml-4 flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Level {approver.approver_order}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          (approver.status === 'approved' || approver.status === 'paid')
                            ? 'bg-green-100 text-green-800'
                            : approver.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {approver.status.charAt(0).toUpperCase() + approver.status.slice(1).toLowerCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fixed Payment Info */}
            {!isVariablePayment() && (
              <div className="mb-6 pt-6 border-t border-gray-200">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Fixed Payment:</strong> This invoice will go directly to finance team and payment processor. No approvers required.
                  </p>
                </div>
              </div>
            )}

            {/* Update Button */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setCreatedInvoice(null);
                  setProposal(null);
                  setReferenceNumber('');
                  setAdditionalAttachments([]);
                  setApprovers(['']);
                  setApproverSearchTerms(['']);
                  setAmount('');
                }}
                className="btn-secondary"
              >
                Create New Invoice
              </button>
              <button
                onClick={handleUpdateInvoice}
                className="btn-primary"
                disabled={updating || !amount.trim() || (isVariablePayment() && !createdInvoice.approvers?.length && approvers.every(a => a === ''))}
              >
                {updating ? 'Sending...' : 'Send for Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Create Invoice</h3>
                <button
                  onClick={handleCloseModal}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <form onSubmit={handleSubmitInvoice} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice File <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-400 transition-colors">
                    <div className="space-y-1 text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="invoice-file"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                        >
                          <span>Upload a file</span>
                          <input
                            id="invoice-file"
                            ref={fileInputRef}
                            name="invoice-file"
                            type="file"
                            className="sr-only"
                            accept=".pdf,.doc,.docx,.xls,.xlsx"
                            onChange={handleFileChange}
                            disabled={submitting}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PDF, DOC, DOCX, XLS, XLSX up to 10MB
                      </p>
                    </div>
                  </div>
                  {invoiceFile && (
                    <div className="mt-3 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                      <div className="flex items-center space-x-2">
                        <Upload className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700 truncate">{invoiceFile.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(invoiceFile.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setInvoiceFile(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-secondary w-full sm:w-auto"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary w-full sm:w-auto"
                    disabled={submitting || !invoiceFile}
                  >
                    {submitting ? 'Uploading...' : 'Upload Invoice'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      <AttachmentViewer
        isOpen={showAttachmentViewer}
        url={currentAttachmentUrl}
        filename={currentAttachmentName}
        onClose={closeAttachmentViewer}
      />
    </div>
  );
};

export default CreateFinancialPowerInvoice;

